import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { useStoreContext } from 'features/store';

import { ServiceMode } from 'generated/graphql-gateway';
import { selectors, useAppSelector } from 'state/global-state';
import { useOrderContext } from 'state/order';
import { useServiceModeLive } from 'state/service-mode';

import {
  isCheckoutStoreRepriceStale,
  isDeliveryQuoteOrAddressStale,
} from './delivery-reprice-utils';
import { useCheckoutContext } from './use-checkout-context';

export const useCheckoutLoadingState = () => {
  const {
    isSubmittingOrder,
    isBuildingCommitOrderPayload,
    isPaymentRedirectPending,
    isLoadingPaymentMethods,
    isSavingPaymentMethods,
    isOrderLoading,
    isRepricedOrderLoading,
    isMenuLoading,
    isSelectingStore,
    isFetchingStore,
    serverOrder,
  } = useCheckoutContext();
  const { store } = useStoreContext();
  const {
    checkoutRepriceRequestId,
    checkoutRepriceRequestCompletedId,
    deliveryAddress,
    quoteId,
  } = useOrderContext();
  const serviceMode = useServiceModeLive();
  const { isMinimalCartStoreRepricing } = useMinimalCart();

  // Read the pricing state from Redux (set by price order)
  const isPricingOrderFromCart = useAppSelector(selectors.ordering.selectIsPricingOrder);
  const selectedStoreNumber = store?.number ?? null;
  const isStoreChangeRepricePending = Boolean(
    checkoutRepriceRequestId > checkoutRepriceRequestCompletedId &&
      isCheckoutStoreRepriceStale({
        selectedStoreNumber,
        serverOrder,
      })
  );
  const isDeliveryRepriceRequestPending = Boolean(
    checkoutRepriceRequestId > checkoutRepriceRequestCompletedId &&
      serviceMode === ServiceMode.DELIVERY &&
      isDeliveryQuoteOrAddressStale({
        quoteId,
        serverOrderDeliveryQuoteId: serverOrder?.delivery?.quoteId,
        deliveryAddress,
        serverOrderDropoff: serverOrder?.delivery?.dropoff,
      })
  );
  const isCheckoutMinimalCartStoreRepricing = Boolean(
    checkoutRepriceRequestId > checkoutRepriceRequestCompletedId && isMinimalCartStoreRepricing
  );

  // Combine local order loading state with cart pricing state
  const isOrderPriceLoading =
    isOrderLoading ||
    isRepricedOrderLoading ||
    isPricingOrderFromCart ||
    isCheckoutMinimalCartStoreRepricing ||
    isStoreChangeRepricePending ||
    isDeliveryRepriceRequestPending;

  const isCheckoutInitializing =
    isOrderPriceLoading ||
    isLoadingPaymentMethods ||
    isMenuLoading ||
    isSelectingStore ||
    isFetchingStore ||
    isPaymentRedirectPending;

  const isPlaceOrderLoading =
    isOrderPriceLoading ||
    isSubmittingOrder ||
    isBuildingCommitOrderPayload ||
    isLoadingPaymentMethods;
  const isPlaceOrderDisabled = isPlaceOrderLoading || isPaymentRedirectPending;

  return {
    isOrderPriceLoading,
    isSubmittingOrder,
    isBuildingCommitOrderPayload,
    isPaymentRedirectPending,
    isLoadingPaymentMethods,
    isSavingPaymentMethods,
    isCheckoutLoading: isCheckoutInitializing || isSubmittingOrder || isBuildingCommitOrderPayload,
    isPlaceOrderDisabled,
    isPlaceOrderLoading,
  };
};
